import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound,
  Plus,
  Copy,
  Power,
  PowerOff,
  Trash2,
  Eye,
  EyeOff,
  Zap,
  BookOpen,
  ExternalLink,
  Activity,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from "lucide-react";

import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function ApiKeys() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justCreated, setJustCreated] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/api-keys").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggle = async (row) => {
    try {
      await api.patch(`/api-keys/${row.id}`, { active: !row.active });
      toast.success(!row.active ? "Enabled" : "Disabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Revoke API key "${row.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api-keys/${row.id}`);
      toast.success("Revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setCallback = async (row) => {
    const next = window.prompt("Callback URL (Eden endpoint that will receive delivery status). Leave empty to remove.", row.callback_url || "");
    if (next === null) return;
    try {
      await api.patch(`/api-keys/${row.id}`, { callback_url: next.trim() });
      toast.success("Callback URL updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setInboundCallback = async (row) => {
    const next = window.prompt(
      "Inbound Callback URL — POSTed when the patient replies with a button tap, list pick, or free text. Leave empty to remove.",
      row.inbound_callback_url || ""
    );
    if (next === null) return;
    try {
      await api.patch(`/api-keys/${row.id}`, { inbound_callback_url: next.trim() });
      toast.success("Inbound URL updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setPrefix = async (row) => {
    const next = window.prompt(
      "Template prefix — only templates starting with this string can be sent using this key.\n\nExamples: hrms_, eden_, marketing_\n\nLeave empty to allow ALL approved templates.",
      row.allowed_template_prefix || ""
    );
    if (next === null) return;
    const trimmed = next.trim();
    try {
      await api.patch(`/api-keys/${row.id}`, { allowed_template_prefix: trimmed });
      toast.success(trimmed ? `Prefix set to '${trimmed}'` : "Prefix cleared — any template allowed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setRateLimit = async (row) => {
    const next = window.prompt(
      "Rate limit (sends per minute). Range 1–1000.",
      String(row.rate_limit_per_min || 60)
    );
    if (next === null) return;
    const n = parseInt(next.trim(), 10);
    if (!n || n < 1 || n > 1000) return toast.error("Enter a number between 1 and 1000");
    try {
      await api.patch(`/api-keys/${row.id}`, { rate_limit_per_min: n });
      toast.success(`Rate limit set to ${n}/min`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const rotateSecret = async (row) => {
    if (!window.confirm(`Rotate the HMAC signing secret for "${row.name}"?\n\nThe integrator will need to update their webhook verifier with the new secret. Old secret stops working immediately.`)) return;
    try {
      const r = await api.patch(`/api-keys/${row.id}`, { rotate_signing_secret: true });
      const newSecret = r.data?.signing_secret;
      if (newSecret) {
        window.prompt(
          "NEW signing secret — copy it now, it won't be shown again from this page after reload:",
          newSecret
        );
      }
      toast.success("Signing secret rotated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="px-10 py-10">
      <header className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Integration</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="apikeys-title">
            API Keys
          </h1>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            Issue a key so external systems like <span className="font-mono text-foreground">Eden</span> can send WhatsApp templates to patients on your behalf.
          </p>
        </div>
        <CreateKeyDialog onCreated={(k) => { setJustCreated(k); load(); }} />
      </header>

      {justCreated && <NewKeyBanner apiKey={justCreated} onClose={() => setJustCreated(null)} />}

      <Tabs defaultValue="keys" className="mt-2">
        <TabsList className="rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="keys" data-testid="tab-keys" className="rounded-none border-b-2 border-transparent px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent">
            <KeyRound className="mr-2 h-4 w-4" /> Keys
          </TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs" className="rounded-none border-b-2 border-transparent px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent">
            <BookOpen className="mr-2 h-4 w-4" /> Eden Integration Docs
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-none border-b-2 border-transparent px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent">
            <Activity className="mr-2 h-4 w-4" /> Eden Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="mt-6">
          <div className="border border-border">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Name</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Key</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Template Prefix</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Rate</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Status Callback</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Inbound Callback</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Uses</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Last used</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Status</TableHead>
                  <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.2em]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="py-16 text-center text-muted-foreground">
                    No API keys yet. Create one and paste it into Eden.
                  </TableCell></TableRow>
                ) : (
                  rows.map((k) => (
                    <TableRow key={k.id} className="border-border" data-testid={`apikey-row-${k.id}`}>
                      <TableCell className="font-display font-bold">{k.name}</TableCell>
                      <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                      <TableCell className="font-mono text-xs">
                        {k.allowed_template_prefix
                          ? <span className="border border-border bg-secondary px-1.5 py-0.5">{k.allowed_template_prefix}</span>
                          : <span className="text-muted-foreground/70">any</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{k.rate_limit_per_min ?? 60}/min</TableCell>
                      <TableCell className="max-w-[200px] truncate font-mono text-xs text-muted-foreground">
                        {k.callback_url || <span className="text-muted-foreground/70">not set</span>}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate font-mono text-xs text-muted-foreground">
                        {k.inbound_callback_url || <span className="text-muted-foreground/70">not set</span>}
                      </TableCell>
                      <TableCell className="font-mono">{k.uses ?? 0}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(k.last_used_at)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${k.active ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${k.active ? "bg-emerald-500" : "bg-red-500"}`} />
                          {k.active ? "Active" : "Revoked"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex flex-wrap justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setPrefix(k)} className="h-8 rounded-sm text-xs" data-testid={`edit-prefix-${k.id}`}>
                            Prefix
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setRateLimit(k)} className="h-8 rounded-sm text-xs" data-testid={`edit-rate-${k.id}`}>
                            Rate
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setCallback(k)} className="h-8 rounded-sm text-xs" data-testid={`edit-callback-${k.id}`}>
                            <ExternalLink className="mr-1 h-3.5 w-3.5" /> Status
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setInboundCallback(k)} className="h-8 rounded-sm text-xs" data-testid={`edit-inbound-callback-${k.id}`}>
                            <ExternalLink className="mr-1 h-3.5 w-3.5" /> Inbound
                          </Button>
                          {k.signing_enabled && (
                            <Button variant="ghost" size="sm" onClick={() => rotateSecret(k)} className="h-8 rounded-sm text-xs" data-testid={`rotate-secret-${k.id}`} title="Rotate HMAC signing secret">
                              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Rotate
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => toggle(k)} data-testid={`toggle-${k.id}`}>
                            {k.active ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-emerald-500" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(k)} data-testid={`revoke-${k.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="docs" className="mt-6">
          <DocsPanel />
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <ActivityPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
function ActivityPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (outcome !== "all") params.outcome = outcome;
    api.get("/integrations/logs", { params }).then((r) => setLogs(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, [outcome]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return iso; }
  };

  const stats = useMemo(() => {
    const sent = logs.filter((l) => l.outcome === "sent").length;
    const failed = logs.filter((l) => l.outcome === "failed").length;
    return { sent, failed, total: logs.length };
  }, [logs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {["all", "sent", "failed"].map((v) => (
            <button
              key={v}
              onClick={() => setOutcome(v)}
              className={`border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${
                outcome === v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`activity-filter-${v}`}
            >
              {v}
            </button>
          ))}
          <div className="ml-3 flex gap-3 text-[11px] text-muted-foreground">
            <span data-testid="activity-stat-sent" className="text-emerald-600 dark:text-emerald-400">✓ {stats.sent} sent</span>
            <span data-testid="activity-stat-failed" className="text-red-600 dark:text-red-400">✗ {stats.failed} failed</span>
            <span>· showing {stats.total}</span>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-sm" data-testid="activity-refresh-btn">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="w-6"></TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">When</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Key</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">To</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Template</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Ref</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Outcome</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-16 text-center text-muted-foreground">
                No Eden calls yet — every request to <span className="font-mono">/integrations/send-template</span> is logged here.
              </TableCell></TableRow>
            ) : (
              logs.map((l) => {
                const isOpen = expanded === l.id;
                const isFail = l.outcome === "failed";
                return (
                  <React.Fragment key={l.id}>
                    <TableRow className="border-border" data-testid={`activity-row-${l.id}`}>
                      <TableCell>
                        <button onClick={() => setExpanded(isOpen ? null : l.id)} className="text-muted-foreground hover:text-foreground" data-testid={`activity-expand-${l.id}`}>
                          {isOpen ? "▾" : "▸"}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">{fmt(l.at)}</TableCell>
                      <TableCell className="text-xs">{l.api_key_name || "—"}</TableCell>
                      <TableCell className="font-mono text-[11px]">+{l.to}</TableCell>
                      <TableCell className="font-mono text-[11px]">{l.template_name}</TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground">{l.reference_id || "—"}</TableCell>
                      <TableCell>
                        {isFail ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 dark:text-red-400">
                            <XCircle className="h-3.5 w-3.5" /> {l.status_code || "FAIL"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Sent
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={`max-w-xs truncate text-[11px] ${isFail ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>
                        {isFail ? (l.error_message || l.error_code) : (l.wamid ? `wamid ${l.wamid.slice(-16)}` : "ok")}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="border-border bg-secondary/40">
                        <TableCell colSpan={8}>
                          <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[10px] text-muted-foreground">
{JSON.stringify(l, null, 2)}
                          </pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}



function CreateKeyDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [callback, setCallback] = useState("");
  const [inbound, setInbound] = useState("");
  const [prefix, setPrefix] = useState("");
  const [rateLimit, setRateLimit] = useState(60);
  const [signingEnabled, setSigningEnabled] = useState(true);
  const [authType, setAuthType] = useState("");
  const [authValue, setAuthValue] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const payload = {
        name,
        callback_url: callback.trim() || null,
        inbound_callback_url: inbound.trim() || null,
        allowed_template_prefix: prefix.trim() || null,
        rate_limit_per_min: Number(rateLimit) || 60,
        signing_enabled: signingEnabled,
      };
      if (authType && authValue.trim()) {
        payload.callback_auth = { type: authType, value: authValue.trim() };
      }
      const r = await api.post("/api-keys", payload);
      onCreated(r.data);
      setOpen(false);
      setName(""); setCallback(""); setInbound("");
      setPrefix(""); setRateLimit(60); setSigningEnabled(true);
      setAuthType(""); setAuthValue("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm" data-testid="new-apikey-btn">
          <Plus className="mr-2 h-4 w-4" /> New API key
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Register a new integrating system</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">System name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Eden HRMS" className="mt-1 rounded-sm" data-testid="apikey-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">Template prefix (namespace)</Label>
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="hrms_" className="mt-1 rounded-sm font-mono" data-testid="apikey-prefix-input" />
              <p className="mt-1 text-[10px] text-muted-foreground">Only templates starting with this prefix can be sent by this key. Leave blank for no restriction.</p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Rate limit (sends per minute)</Label>
            <Input
              type="number" min={1} max={1000}
              value={rateLimit}
              onChange={(e) => setRateLimit(e.target.value)}
              className="mt-1 w-32 rounded-sm font-mono"
              data-testid="apikey-rate-input"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">429 returned if exceeded. Default 60.</p>
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Webhook callbacks</div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">Status callback URL</Label>
                <Input value={callback} onChange={(e) => setCallback(e.target.value)} placeholder="https://your-system.example.com/webhooks/waba-status" className="mt-1 rounded-sm font-mono text-xs" data-testid="apikey-callback-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">Inbound callback URL</Label>
                <Input value={inbound} onChange={(e) => setInbound(e.target.value)} placeholder="https://your-system.example.com/webhooks/waba-inbound" className="mt-1 rounded-sm font-mono text-xs" data-testid="apikey-inbound-input" />
              </div>
              <label className="flex items-center gap-2 text-xs" data-testid="apikey-signing-toggle-label">
                <input
                  type="checkbox"
                  checked={signingEnabled}
                  onChange={(e) => setSigningEnabled(e.target.checked)}
                  data-testid="apikey-signing-toggle"
                />
                <span className="font-bold uppercase tracking-[0.15em] text-muted-foreground">Sign outgoing webhooks with HMAC-SHA256</span>
              </label>
              <p className="-mt-1 text-[10px] text-muted-foreground">
                We add <code className="font-mono">X-Waba-Signature: sha256=&lt;hex&gt;</code>, <code className="font-mono">X-Waba-Timestamp</code>, and <code className="font-mono">X-Waba-Key-Id</code> headers. Your webhook can verify authenticity using the signing secret shown once at creation.
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Extra auth on your webhook (optional)</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">Type</Label>
                <Select value={authType || "none"} onValueChange={(v) => setAuthType(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="apikey-auth-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="bearer">Bearer</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">Value</Label>
                <Input value={authValue} onChange={(e) => setAuthValue(e.target.value)} disabled={!authType} placeholder="token / base64(user:pass)" className="mt-1 rounded-sm font-mono text-xs" data-testid="apikey-auth-value" />
              </div>
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground">We attach this as <code className="font-mono">Authorization</code> header on every callback POST to your endpoint.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="confirm-create-apikey">
            {saving ? "Creating…" : "Create key"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewKeyBanner({ apiKey, onClose }) {
  const [visible, setVisible] = useState(true);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey.key);
      toast.success("Key copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="mb-6 border-2 border-primary bg-secondary p-6" data-testid="new-key-banner">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-primary">
        <Zap className="h-4 w-4" /> Save this key now — you won&apos;t see it again
      </div>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 truncate border border-border bg-background px-3 py-2 font-mono text-sm">
          {visible ? apiKey.key : apiKey.key.replace(/./g, "•")}
        </code>
        <Button variant="ghost" size="icon" onClick={() => setVisible(!visible)} aria-label="Toggle" data-testid="toggle-key-visibility">
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
        <Button variant="outline" onClick={copy} className="rounded-sm" data-testid="copy-new-key">
          <Copy className="mr-2 h-4 w-4" /> Copy
        </Button>
        <Button variant="ghost" onClick={onClose} className="rounded-sm">Dismiss</Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Store this in Eden&apos;s configuration. If you lose it, revoke this key and create a new one.
      </p>
    </div>
  );
}

function DocsPanel() {
  const base = API_BASE;
  const [approvedTemplates, setApprovedTemplates] = useState([]);

  useEffect(() => {
    api.get("/templates").then((r) => setApprovedTemplates(r.data.filter((t) => t.status === "APPROVED")));
  }, []);

  const sendCurl = `curl -X POST "${base}/integrations/send-template" \\
  -H "X-API-Key: wcrm_live_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "9876543210",
    "template_name": "appointment_reminder",
    "language": "en_US",
    "variables": {"1": "Rajesh", "2": "12 Feb", "3": "10:30 AM", "4": "Dr. Kaur"},
    "reference_id": "eden_appt_98421",
    "patient_name": "Rajesh Sharma"
  }'`;

  const statusCurl = `curl -H "X-API-Key: wcrm_live_YOUR_KEY_HERE" \\
  "${base}/integrations/message-status/wamid.HBgLOTE5ODc..."`;

  const listCurl = `curl -H "X-API-Key: wcrm_live_YOUR_KEY_HERE" \\
  "${base}/integrations/templates"`;

  const callbackPayload = `{
  "wamid": "wamid.HBgLOTE5ODc...",
  "status": "delivered",
  "to": "919876543210",
  "template_name": "appointment_reminder",
  "reference_id": "eden_appt_98421",
  "timestamp": "2026-02-12T10:30:45.123Z"
}`;

  const copy = (t, label) => {
    navigator.clipboard.writeText(t).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Copy failed"));
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <DocCard title="1 · Send a template message" testid="doc-send">
          <p>Use this endpoint whenever Eden creates, reschedules, cancels an appointment, or fires a daily reminder.</p>
          <Endpoint method="POST" path="/api/integrations/send-template" />
          <CodeBlock code={sendCurl} onCopy={() => copy(sendCurl, "Example")} testid="copy-send-curl" />
          <SchemaTable
            rows={[
              ["to", "string", "10-digit Indian mobile OR full 12-digit with 91 prefix"],
              ["template_name", "string", "Name of an APPROVED template in the CRM"],
              ["language", "string", "Template language code (default en_US)"],
              ["variables", "object | list", "Body variables — {\"1\":\"Rajesh\",\"2\":\"12 Feb\"} or [\"Rajesh\",\"12 Feb\"]"],
              ["header_variables", "list<string>", "Optional. For templates with variable header text"],
              ["button_url_variables", "object", "Optional. {\"0\":\"abc123\"} to fill dynamic URL button suffix at index 0"],
              ["reference_id", "string", "Your own ID (e.g. Eden appointment_id) — echoed back on callbacks"],
              ["patient_name", "string", "Optional. Shown in the CRM conversation list"],
            ]}
          />
          <p className="text-xs text-muted-foreground">Response: <code className="font-mono">{`{ ok, wamid, to, reference_id, template_name, status }`}</code>. On failure, HTTP 400 with the Meta error message.</p>
        </DocCard>

        <DocCard title="2 · Receive delivery status callbacks" testid="doc-callback">
          <p>If you set a <strong>Status Callback URL</strong> on your API key, we POST this payload every time Meta reports a status change (sent → delivered → read, or failed).</p>
          <Endpoint method="POST" path="https://appointy.eden-office.in/webhooks/waba-status" />
          <CodeBlock code={callbackPayload} onCopy={() => copy(callbackPayload, "Payload")} testid="copy-callback" />
          <p>Match the <code className="font-mono">reference_id</code> back to your appointment record. Respond with HTTP 200 within 8 seconds.</p>
        </DocCard>

        <DocCard title="2b · Receive patient replies (button taps, list picks, text)" testid="doc-inbound-callback">
          <p>If you set an <strong>Inbound Callback URL</strong> on your API key, we POST this payload every time a patient replies to a template you sent — quick-reply buttons, interactive buttons, list picks, or plain text.</p>
          <Endpoint method="POST" path="https://appointy.eden-office.in/webhooks/waba-inbound" />
          <CodeBlock
            code={`{
  "event": "inbound_button_reply",
  "wa_id": "919855747777",
  "from_name": "CHITWANPREET",
  "button": {"id": "5", "title": "5", "type": "interactive_button_reply", "payload": "5"},
  "text": "[interactive] 5",
  "message": {"wamid": "wamid.HBg...", "timestamp": "2026-02-07T...", "type": "interactive"},
  "in_reply_to": {
    "wamid": "wamid.HBg...ORIGINAL",
    "template_name": "eden_feedback_ask_ameesha",
    "reference_id": "eden_appt_98421",
    "sent_at": "2026-02-07T..."
  },
  "channel": "meta",
  "timestamp": "2026-02-07T..."
}`}
            onCopy={() => copy("inbound payload example", "Payload")}
            testid="copy-inbound-callback"
          />
          <p>Use <code className="font-mono">in_reply_to.reference_id</code> to route the reply back to the correct appointment or feedback record. Respond with HTTP 200 within 8 seconds. All forwards are logged under Eden Activity → Inbound.</p>
        </DocCard>

        <DocCard title="3 · Look up a message status on demand" testid="doc-status">
          <Endpoint method="GET" path="/api/integrations/message-status/{wamid}" />
          <CodeBlock code={statusCurl} onCopy={() => copy(statusCurl, "Example")} testid="copy-status-curl" />
        </DocCard>

        <DocCard title="4 · Discover available templates" testid="doc-list">
          <Endpoint method="GET" path="/api/integrations/templates" />
          <CodeBlock code={listCurl} onCopy={() => copy(listCurl, "Example")} testid="copy-list-curl" />
          <p>Returns only APPROVED templates with their component structure so Eden can build the correct variables map.</p>
        </DocCard>
      </div>

      <aside className="space-y-6">
        <div className="border border-border p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Base URL</div>
          <code className="mt-2 block break-all border border-border bg-secondary px-2 py-1.5 font-mono text-xs" data-testid="doc-base-url">{base}</code>
          <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Auth header</div>
          <code className="mt-2 block break-all border border-border bg-secondary px-2 py-1.5 font-mono text-xs">X-API-Key: wcrm_live_...</code>
          <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Rate</div>
          <p className="mt-2 text-xs text-muted-foreground">Meta enforces its own tier-based rate limits. We do not throttle at the CRM level.</p>
        </div>
        <div className="border border-border p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Approved templates</div>
          {approvedTemplates.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No approved templates yet. Create and submit templates in the Templates section.</p>
          ) : (
            <ul className="mt-3 space-y-1.5 font-mono text-xs">
              {approvedTemplates.map((t) => (
                <li key={t.name} className="flex items-center justify-between gap-2">
                  <span>{t.name}</span>
                  <span className="text-muted-foreground">{t.language}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function DocCard({ title, testid, children }) {
  return (
    <section className="border border-border p-6" data-testid={testid}>
      <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
      <div className="mt-3 space-y-3 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

function Endpoint({ method, path }) {
  const color = method === "POST" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40" : "bg-primary/10 text-primary border-primary/40";
  return (
    <div className="flex items-center gap-2">
      <span className={`border px-2 py-0.5 font-mono text-[10px] font-bold ${color}`}>{method}</span>
      <code className="font-mono text-xs text-foreground">{path}</code>
    </div>
  );
}

function CodeBlock({ code, onCopy, testid }) {
  return (
    <div className="relative border border-border bg-foreground/[0.03]">
      <Button variant="ghost" size="icon" onClick={onCopy} aria-label="Copy" className="absolute right-1 top-1 h-7 w-7" data-testid={testid}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
      <pre className="overflow-x-auto p-4 pr-10 font-mono text-xs leading-relaxed text-foreground">{code}</pre>
    </div>
  );
}

function SchemaTable({ rows }) {
  return (
    <div className="border border-border">
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Field</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Type</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(([f, t, d]) => (
            <TableRow key={f} className="border-border">
              <TableCell className="font-mono text-xs">{f}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">{t}</TableCell>
              <TableCell className="text-xs">{d}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
